import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  TextField,
  Button,
  Typography,
  MenuItem,
  Paper,
  Checkbox,
  FormControlLabel,
  Avatar,
  IconButton,
} from "@mui/material";
import { PhotoCamera } from "@mui/icons-material";
import { toast } from "react-toastify";
import apiClient from "../utils/apiClient";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { getImageFullUrl } from "../utils/common";

const VendorBusinessDetails = () => {
  const [form, setForm] = useState({
    seller_type: "",
    pan_number: "",
    gst_number: "",
    businessName: '',
    office_address_line1: "",
    office_address_line2: "",
    office_pincode: "",
    office_city: "",
    office_state: "",
    office_country: "INDIA",

    pickup_address_line1: "",
    pickup_address_line2: "",
    pickup_pincode: "",
    pickup_city: "",
    pickup_state: "",
    pickup_country: "INDIA",

    same_as_office_address: false,

    pan_card_file: null,
    aadhaar_card_file: null,
    signature_file: null,
    gst_certificate_file: null,
  });

  const [preview, setPreview] = useState({});
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false); // submit
  const [fetchLoading, setFetchLoading] = useState(false); // fetch
 
  const fetchDetails = async () => {
    try {
      setFetchLoading(true);

      const res = await apiClient.get("/api/vendor/vendor-business-details");

      if (res?.data?.data?.id) {
        setForm({ ...res.data.data });
      }
    } catch (err) {
      toast.error("Failed to fetch data");
    } finally {
      setFetchLoading(false);
    }
  };

  useEffect(() => {
    fetchDetails()
  }, [])
  // ✅ File Handler
  const handleFile = (key, file) => {
    if (!file) return;

    if (
      !["image/png", "image/jpeg", "image/jpg"].includes(
        file.type
      )
    ) {
      toast.error("Only JPG, PNG, JPEG allowed");
      return;
    }

    setForm((prev) => ({ ...prev, [key]: file }));

    // cleanup old preview
    if (preview[key]) URL.revokeObjectURL(preview[key]);

    setPreview((prev) => ({
      ...prev,
      [key]: URL.createObjectURL(file),
    }));
  };

  // ✅ Input change
  const handleChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  // ✅ Same Address
  const handleSameAddress = (checked) => {
    setForm((prev) => ({
      ...prev,
      same_as_office_address: checked,
      pickup_address_line1: checked ? prev.office_address_line1 : "",
      pickup_address_line2: checked ? prev.office_address_line2 : "",
      pickup_pincode: checked ? prev.office_pincode : "",
      pickup_city: checked ? prev.office_city : "",
      pickup_state: checked ? prev.office_state : "",
    }));
  };

  // ✅ Validation
  const validate = () => {
    let temp = {};

    if (!form.pan_number) temp.pan_number = "Pan number required";
    if (!form.gst_number) temp.gst_number = "GST number required";
    if (!form.seller_type) temp.seller_type = "Seller Type required";
    if (!form.businessName) temp.businessName = "Business name required";

    if (form.pan_number && !/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(form.pan_number)) {
      temp.pan_number = "Invalid PAN";
    }

    if (form.gst_number && !/^[0-9A-Z]{15}$/.test(form.gst_number)) {
      temp.gst_number = "Invalid GST";
    }

    // Office Pincode
    if (!form.office_pincode) {
      temp.office_pincode = "Office pincode required";
    } else if (!/^[1-9][0-9]{5}$/.test(form.office_pincode)) {
      temp.office_pincode = "Invalid Pincode";
    }

    // Pickup Pincode
    if (!form.same_as_office_address) {
      if (!form.pickup_pincode) {
        temp.pickup_pincode = "Pickup pincode required";
      } else if (!/^[1-9][0-9]{5}$/.test(form.pickup_pincode)) {
        temp.pickup_pincode = "Invalid Pincode";
      }
    }

    // Office Address
    if (!form.office_address_line1) temp.office_address_line1 = "Office address line 1 required";
    if (!form.office_address_line2) temp.office_address_line2 = "Office address line 2 required";
    // if (!form.office_pincode) temp.office_pincode = "Office pincode required";
    if (!form.office_city) temp.office_city = "Office city required";
    if (!form.office_state) temp.office_state = "Office state required";

    // Pickup Address (if not same)
    if (!form.same_as_office_address) {
      if (!form.pickup_address_line1) temp.pickup_address_line1 = "Pickup address line 1 required";
      if (!form.pickup_address_line2) temp.pickup_address_line2 = "Pickup address line 2 required";
      // if (!form.pickup_pincode) temp.pickup_pincode = "Pickup pincode required";
      if (!form.pickup_city) temp.pickup_city = "Pickup city required";
      if (!form.pickup_state) temp.pickup_state = "Pickup state required";
    }

    // Files (IMPORTANT fix: allow existing URL also)
    if (!form.pan_card_file) temp.pan_card_file = "Pan card file required";
    if (!form.aadhaar_card_file) temp.aadhaar_card_file = "Aadhaar card file required";
    if (!form.signature_file) temp.signature_file = "Signature file required";
    if (!form.gst_certificate_file) temp.gst_certificate_file = "Gst certificate file required";


    setErrors(temp);
    return Object.keys(temp).length === 0;
  };

  // ✅ Submit
  const handleSubmit = () => {
    if (!validate()) return;
    setLoading(true);
    const formData = new FormData();

    formData.append("businessName", form.businessName);
    formData.append("seller_type", form.seller_type);
    formData.append("pan_number", form.pan_number);
    formData.append("gst_number", form.gst_number);
    formData.append("office_address_line1", form.office_address_line1);
    formData.append("office_address_line2", form.office_address_line2);
    formData.append("office_pincode", form.office_pincode);
    formData.append("office_city", form.office_city);
    formData.append("office_state", form.office_state);
    formData.append("office_country", form.office_country);
    formData.append("pickup_address_line1", form.pickup_address_line1);
    formData.append("pickup_address_line2", form.pickup_address_line2);
    formData.append("pickup_pincode", form.pickup_pincode);
    formData.append("pickup_city", form.pickup_city);
    formData.append("pickup_state", form.pickup_state);
    formData.append("pickup_country", form.pickup_country);
    formData.append("same_as_office_address", form.same_as_office_address);

    // append file only if it's a File object (not just a string URL from DB)
    if (form.pan_card_file instanceof File) {
      formData.append("pan_card_file", form.pan_card_file);
    }
    if (form.aadhaar_card_file instanceof File) {
      formData.append("aadhaar_card_file", form.aadhaar_card_file);
    }
    if (form.signature_file instanceof File) {
      formData.append("signature_file", form.signature_file);
    }
    if (form.gst_certificate_file instanceof File) {
      formData.append("gst_certificate_file", form.gst_certificate_file);
    }

    apiClient
      .post("/api/vendor/business-details", formData)
      .then(() => toast.success("Submitted successfully"))
      .catch((err) =>
        toast.error(err.response?.data?.message || "Error")
      ).finally(() => {
        setLoading(false);
      });
  };

  const FileUpload = ({ label, field }) => (
    <Box>
      <Typography variant="body2" mb={2}>
        {label}
      </Typography>
      <Box sx={{
        border: "2px dashed #ccc",
        borderRadius: 2,
        p: 2,
        textAlign: "center",
        cursor: "pointer",
        transition: "0.3s",
        "&:hover": {
          borderColor: "primary.main",
          bgcolor: "#f9f9f9",
        },
      }}>

        <Box
          component="label"

        >
          <input
            type="file"
            hidden
            accept="image/*"
            onChange={(e) => handleFile(field, e.target.files[0])}
          />
          {preview[field] &&
            <Box>
              {/* ✅ Show image preview if image */}
              {form[field]?.type?.startsWith("image") ? (
                <Box
                  component="img"
                  src={preview[field]}
                  alt="preview"
                  sx={{ width: "100%", height: 100, objectFit: "cover", borderRadius: 1 }}
                />
              ) : (
                <InsertDriveFileIcon sx={{ fontSize: 40, color: "gray" }} />
              )}

              <Typography variant="caption" display="block" mt={1}>
                {form[field]?.name}
              </Typography>
            </Box>
          }
          {!preview[field] && form[field] &&
            <Box>
              {/* ✅ Show image preview if image */}
              {form[field] ? (
                <Box
                  component="img"
                  src={getImageFullUrl(form[field])}
                  alt="preview"
                  sx={{ width: "100%", height: 100, objectFit: "cover", borderRadius: 1 }}
                />
              ) : (
                <InsertDriveFileIcon sx={{ fontSize: 40, color: "gray" }} />
              )}

              <Typography variant="caption" display="block" mt={1}>
                {form[field]?.name}
              </Typography>
            </Box>
          }

          {!preview[field] && !form[field] &&
            <Box>
              <CloudUploadIcon sx={{ fontSize: 40, color: "gray" }} />
              <Typography variant="body2">
                Click to upload
              </Typography>
              <Typography variant="caption" color="text.secondary">
                JPG, PNG allowed
              </Typography>
            </Box>
          }
        </Box>
      </Box>

      {errors[field] && (
        <Typography color="error" variant="caption">
          {errors[field]}
        </Typography>
      )}
    </Box>
  );


  return (
    <Paper sx={{ p: 3, borderRadius: 3 }}>
      {/* <Typography variant="h4" mb={2}>
        Vendor Business Details
      </Typography>
      <hr /> */}

      {fetchLoading ? (
        <Typography>Loading data...</Typography>
      ) : (
        <Grid container spacing={2}>
          <Grid item size={{ xs: 12 }}>
            <Typography variant="h6">Vendor KYC Details</Typography>
          </Grid>
          {/* Seller Type */}
          <Grid item size={{ xs: 6 }}>
            <TextField
              select
              label="Seller Type"
              fullWidth
              value={form.seller_type}
              onChange={(e) => handleChange("seller_type", e.target.value)}
              error={!!errors.seller_type}
              helperText={errors.seller_type}
            >
              <MenuItem value="individual">Individual</MenuItem>
              <MenuItem value="business">Business</MenuItem>
              <MenuItem value="manufacturers">Manufacturers</MenuItem>
              <MenuItem value="wholesalers">Wholesalers</MenuItem>
              <MenuItem value="retailers">Retailers</MenuItem>
              <MenuItem value="seller">Seller</MenuItem>
              <MenuItem value="resellers">Resellers</MenuItem>
            </TextField>
          </Grid>

          <Grid item size={{ xs: 6 }}>
            <TextField
              label="Business Name"
              fullWidth
              value={form.businessName}
              onChange={(e) =>
                handleChange("businessName", e.target.value)
              }
              error={!!errors.businessName}
              helperText={errors.businessName}
            />
          </Grid>

          {/* PAN + GST */}
          <Grid item size={{ xs: 6 }}>
            <TextField
              label="PAN Number"
              fullWidth
              value={form.pan_number}
              onChange={(e) =>
                handleChange("pan_number", e.target.value.toUpperCase())
              }
              error={!!errors.pan_number}
              helperText={errors.pan_number}
            />
          </Grid>

          <Grid item size={{ xs: 6 }}>
            <TextField
              label="GST Number"
              fullWidth
              value={form.gst_number}
              onChange={(e) =>
                handleChange("gst_number", e.target.value.toUpperCase())
              }
              error={!!errors.gst_number}
              helperText={errors.gst_number}
            />
          </Grid>

          {/* Office Address */}
          <Grid item size={{ xs: 12 }}>
            <Typography variant="h6">Office Address</Typography>
          </Grid>

          <Grid item size={{ xs: 12 }}>
            <TextField
              label="Address Line 1"
              fullWidth
              value={form.office_address_line1}
              onChange={(e) =>
                handleChange("office_address_line1", e.target.value)
              }
              error={!!errors.office_address_line1}
              helperText={errors.office_address_line1}
            />
          </Grid>
          <Grid item size={{ xs: 12 }}>
            <TextField
              label="Address Line 2"
              fullWidth
              value={form.office_address_line2}
              onChange={(e) =>
                handleChange("office_address_line2", e.target.value)
              }
              error={!!errors.office_address_line2}
              helperText={errors.office_address_line2}
            />
          </Grid>

          <Grid item size={{ xs: 6 }}>
            <TextField
              label="Pincode"
              fullWidth
              value={form.office_pincode}
              onChange={(e) =>
                handleChange("office_pincode", e.target.value)
              }
              error={!!errors.office_pincode}
              helperText={errors.office_pincode}
            />
          </Grid>

          <Grid item size={{ xs: 6 }}>
            <TextField
              label="City"
              fullWidth
              value={form.office_city}
              onChange={(e) =>
                handleChange("office_city", e.target.value)
              }
              error={!!errors.office_city}
              helperText={errors.office_city}
            />
          </Grid>

          <Grid item size={{ xs: 12 }}>
            <TextField
              label="State"
              fullWidth
              value={form.office_state}
              onChange={(e) =>
                handleChange("office_state", e.target.value)
              }
              error={!!errors.office_state}
              helperText={errors.office_state}
            />
          </Grid>

          {/* Same Address */}
          <Grid item size={{ xs: 12 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={form.same_as_office_address}
                  onChange={(e) =>
                    handleSameAddress(e.target.checked)
                  }
                />
              }
              label="Pickup same as office"
            />
          </Grid>

          {/* Pickup */}
          {!form.same_as_office_address && (
            <>
              <Grid item size={{ xs: 12 }}>
                <Typography variant="h6">Pickup Address</Typography>
              </Grid>

              <Grid item size={{ xs: 12 }}>
                <TextField
                  label="Address Line 1"
                  fullWidth
                  value={form.pickup_address_line1}
                  onChange={(e) =>
                    handleChange("pickup_address_line1", e.target.value)
                  }
                  error={!!errors.pickup_address_line1}
                  helperText={errors.pickup_address_line1}
                />
              </Grid>
              <Grid item size={{ xs: 12 }}>
                <TextField
                  label="Address Line 2"
                  fullWidth
                  value={form.pickup_address_line2}
                  onChange={(e) =>
                    handleChange("pickup_address_line2", e.target.value)
                  }
                  error={!!errors.pickup_address_line2}
                  helperText={errors.pickup_address_line2}
                />
              </Grid>

              <Grid item size={{ xs: 6 }}>
                <TextField
                  label="Pincode"
                  fullWidth
                  value={form.pickup_pincode}
                  onChange={(e) =>
                    handleChange("pickup_pincode", e.target.value)
                  }
                  error={!!errors.pickup_pincode}
                  helperText={errors.pickup_pincode}
                />
              </Grid>

              <Grid item size={{ xs: 6 }}>
                <TextField
                  label="City"
                  fullWidth
                  value={form.pickup_city}
                  onChange={(e) =>
                    handleChange("pickup_city", e.target.value)
                  }
                  error={!!errors.pickup_city}
                  helperText={errors.pickup_city}
                />
              </Grid>

              <Grid item size={{ xs: 12 }}>
                <TextField
                  label="State"
                  fullWidth
                  value={form.pickup_state}
                  onChange={(e) =>
                    handleChange("pickup_state", e.target.value)
                  }
                  error={!!errors.pickup_state}
                  helperText={errors.pickup_state}
                />
              </Grid>
            </>
          )}

          {/* Documents */}
          <Grid item size={{ xs: 12 }}>
            <Typography variant="h6">Upload Documents</Typography>
          </Grid>

          <Grid item size={{ xs: 6, md: 3 }}>
            <FileUpload label="PAN Card" field="pan_card_file" />
          </Grid>

          <Grid item size={{ xs: 6, md: 3 }}>
            <FileUpload label="Aadhaar Card" field="aadhaar_card_file" />
          </Grid>

          <Grid item size={{ xs: 6, md: 3 }}>
            <FileUpload label="Signature" field="signature_file" />
          </Grid>

          <Grid item size={{ xs: 6, md: 3 }}>
            <FileUpload label="GST Certificate" field="gst_certificate_file" />
          </Grid>

          {/* Submit */}
          <Grid item size={{ xs: 12 }}>
            <Button variant="contained" onClick={handleSubmit} disabled={loading}
            >
              {loading ? "Submitting..." : "Submit for Approval"}
            </Button>
          </Grid>
        </Grid>
      )}
    </Paper>
  );
};

export default VendorBusinessDetails;